import React, { useState, useEffect } from 'react';
import './AcomodacionData.css';
import baseURL from '../../url';
import Swal from 'sweetalert2';
import Modal from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUtensils, faEdit, faTrash, faSync, faPlus } from '@fortawesome/free-solid-svg-icons';
import moneda from '../../moneda';

// Función helper para formatear precios
const formatPrice = (price) => {
    return `${moneda} ${parseFloat(price).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
};

export default function MesasData({ onRefresh }) {
    const [mesas, setMesas] = useState([]);
    const [mesasFiltradas, setMesasFiltradas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalDetalle, setModalDetalle] = useState(false);
    const [modalEdit, setModalEdit] = useState(false);
    const [modalNew, setModalNew] = useState(false);
    const [mesaSeleccionada, setMesaSeleccionada] = useState(null);
    const [numeroEdit, setNumeroEdit] = useState('');
    const [capacidadEdit, setCapacidadEdit] = useState('');
    const [numeroNew, setNumeroNew] = useState('');
    const [capacidadNew, setCapacidadNew] = useState('');
    const [filtro, setFiltro] = useState('todas'); // 'todas', 'disponibles', 'ocupadas'

    useEffect(() => {
        cargarMesas();
    }, [onRefresh]);

    useEffect(() => {
        aplicarFiltro();
    }, [mesas, filtro]);

    const cargarMesas = async () => {
        try {
            const response = await fetch(`${baseURL}/mesasGet.php`);
            const data = await response.json();
            if (data.success) {
                setMesas(data.mesas);
                setMesasFiltradas(data.mesas);
            }
        } catch (error) {
            console.error('Error al cargar mesas:', error);
        } finally {
            setLoading(false);
        }
    };

    const aplicarFiltro = () => {
        if (filtro === 'todas') {
            setMesasFiltradas(mesas);
        } else if (filtro === 'disponibles') {
            setMesasFiltradas(mesas.filter(m => m.estado === 'Disponible'));
        } else if (filtro === 'ocupadas') {
            setMesasFiltradas(mesas.filter(m => m.estado === 'Ocupada'));
        }
    };

    const handleMesaClick = (mesa) => {
        if (mesa.estado === 'Ocupada' && mesa.idPedido) {
            setMesaSeleccionada(mesa);
            setModalDetalle(true);
        }
    };

    const handleCambiarEstadoMesa = async (idMesa, nuevoEstado) => {
        try {
            const response = await fetch(`${baseURL}/mesaChangeState.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    idMesa,
                    estado: nuevoEstado,
                }),
            });

            const data = await response.json();

            if (data.success) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: data.message,
                });
                setModalDetalle(false);
                cargarMesas();
            } else {
                Swal.fire({
                    icon: data.type === 'warning' ? 'warning' : 'error',
                    title: data.type === 'warning' ? 'Advertencia' : 'Error',
                    text: data.message,
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al cambiar el estado de la acomodación',
            });
        }
    };

    const handleCambiarEstadoPedido = async (idPedido, nuevoEstado) => {
        // Validación: No permitir "Entregado" si estado_cocina no es "Terminado"
        if (nuevoEstado === 'Entregado' && mesaSeleccionada.estado_cocina !== 'Terminado') {
            Swal.fire({
                icon: 'warning',
                title: 'No se puede marcar como Entregado',
                text: 'El pedido debe estar Terminado en cocina primero.',
            });
            return;
        }

        if (nuevoEstado === 'Rechazado') {
            const result = await Swal.fire({
                icon: 'warning',
                title: '¿Está seguro?',
                text: 'Esta acción marcará la acomodación como Disponible. ¿Desea continuar?',
                showCancelButton: true,
                confirmButtonText: 'Sí, continuar',
                cancelButtonText: 'Cancelar',
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        try {
            const response = await fetch(`${baseURL}/pedidoPut.php?idPedido=${idPedido}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    estado: nuevoEstado,
                }),
            });

            const data = await response.json();

            if (data.success) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: 'Estado del pedido actualizado',
                });

                // Si el pedido fue rechazado, marcar la mesa como disponible
                if (nuevoEstado === 'Rechazado' && mesaSeleccionada) {
                    await handleCambiarEstadoMesa(mesaSeleccionada.idMesa, 'Disponible');
                }

                setModalDetalle(false);
                cargarMesas();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: data.error || data.message || 'Error al actualizar el pedido',
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al actualizar el pedido',
            });
        }
    };

    const handleEditarMesa = (mesa) => {
        setMesaSeleccionada(mesa);
        setNumeroEdit(mesa.numero);
        setCapacidadEdit(mesa.capacidad);
        setModalEdit(true);
    };

    const handleActualizarMesa = async (e) => {
        e.preventDefault();

        if (!numeroEdit || !capacidadEdit) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos incompletos',
                text: 'Complete todos los campos',
            });
            return;
        }

        try {
            const response = await fetch(`${baseURL}/mesaPut.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    idMesa: mesaSeleccionada.idMesa,
                    numero: numeroEdit,
                    capacidad: parseInt(capacidadEdit),
                }),
            });

            const data = await response.json();

            if (data.success) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: data.message,
                });
                setModalEdit(false);
                cargarMesas();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: data.message,
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al actualizar la acomodación',
            });
        }
    };

    const handleEliminarMesa = async (idMesa, numero) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: '¿Está seguro?',
            text: `¿Desea eliminar la acomodación ${numero}?`,
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetch(`${baseURL}/mesaDelete.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ idMesa }),
            });

            const data = await response.json();

            if (data.success) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: data.message,
                });
                cargarMesas();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: data.message,
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al eliminar la acomodación',
            });
        }
    };

    const handleCrearMesa = async (e) => {
        e.preventDefault();

        if (!numeroNew || !capacidadNew) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos incompletos',
                text: 'Complete todos los campos',
            });
            return;
        }

        if (parseInt(capacidadNew) < 1) {
            Swal.fire({
                icon: 'warning',
                title: 'Capacidad inválida',
                text: 'La capacidad debe ser al menos 1',
            });
            return;
        }

        try {
            const response = await fetch(`${baseURL}/mesasPost.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    numero: numeroNew,
                    capacidad: parseInt(capacidadNew),
                }),
            });

            const data = await response.json();

            if (data.success) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: data.message,
                });
                setModalNew(false);
                setNumeroNew('');
                setCapacidadNew('');
                cargarMesas();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: data.message,
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al crear la acomodación',
            });
        }
    };

    if (loading) {
        return <div className='loading'>Cargando Acomodaciónes...</div>;
    }

    return (
        <div className='MesasDataContainer'>
            <div className='mesasHeader'>
                <h2>Gestión de Acomodación</h2>
                <div className='mesasHeaderActions'>
                    <div className='filtrosMesas'>
                        <button 
                            className={`btnFiltro ${filtro === 'todas' ? 'active' : ''}`}
                            onClick={() => setFiltro('todas')}
                        >
                            Todas ({mesas.length})
                        </button>
                        <button 
                            className={`btnFiltro ${filtro === 'disponibles' ? 'active' : ''}`}
                            onClick={() => setFiltro('disponibles')}
                        >
                            Disponibles ({mesas.filter(m => m.estado === 'Disponible').length})
                        </button>
                        <button 
                            className={`btnFiltro ${filtro === 'ocupadas' ? 'active' : ''}`}
                            onClick={() => setFiltro('ocupadas')}
                        >
                            Ocupadas ({mesas.filter(m => m.estado === 'Ocupada').length})
                        </button>
                    </div>
                    <div className='mesasHeaderButtons'>
                        <button onClick={() => setModalNew(true)} className='btnNewMesa'>
                            <FontAwesomeIcon icon={faPlus} /> Nueva acomodación
                        </button>
                        <button onClick={cargarMesas} className='btnRefresh'>
                            <FontAwesomeIcon icon={faSync} />
                        </button>
                    </div>
                </div>
            </div>

            {mesasFiltradas.length === 0 ? (
                <div className='noMesas'>
                    <p>
                        {mesas.length === 0 
                            ? 'No hay acomodaciónes registradas. Agregue una nueva acomodación para comenzar.' 
                            : `No hay acomodaciónes ${filtro === 'disponibles' ? 'disponibles' : 'ocupadas'}.`
                        }
                    </p>
                </div>
            ) : (
                <div className='mesasGrid'>
                    {mesasFiltradas.map((mesa) => (
                        <div
                            key={mesa.idMesa}
                            className={`mesaCard ${mesa.estado === 'Ocupada' ? 'ocupada' : 'disponible'}`}
                            onClick={() => handleMesaClick(mesa)}
                        >
                            <div className='mesaIcon'>
                                <FontAwesomeIcon icon={faUtensils} />
                            </div>
                            <div className='mesaInfo'>
                                <h3>{mesa.numero}</h3>
                                <p className='capacidad'>{mesa.capacidad} personas</p>
                                <span className={`estadoBadge ${mesa.estado ? mesa.estado.toLowerCase() : 'disponible'}`}>
                                    {mesa.estado || 'Disponible'}
                                </span>
                            </div>
                            <div className='mesaActions' onClick={(e) => e.stopPropagation()}>
                                <button
                                    onClick={() => handleEditarMesa(mesa)}
                                    className='btnEdit'
                                    title='Editar'
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                    onClick={() => handleEliminarMesa(mesa.idMesa, mesa.numero)}
                                    className='btnDelete'
                                    title='Eliminar'
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal de detalles del pedido */}
            <Modal open={modalDetalle} onClose={() => setModalDetalle(false)} center>
                {mesaSeleccionada && (
                    <div className='modalDetalleContent'>
                        <h2>Acomodación {mesaSeleccionada.numero} - Pedido #{mesaSeleccionada.idPedido}</h2>

                        <div className='detalleSection'>
                            <h3>Información del Cliente</h3>
                            <p><strong>Nombre:</strong> {mesaSeleccionada.nombre}</p>
                            <p><strong>Teléfono:</strong> {mesaSeleccionada.telefono}</p>
                            <p><strong>Email:</strong> {mesaSeleccionada.email}</p>
                            <p><strong>Código:</strong> {mesaSeleccionada.codigo}</p>
                        </div>

                        <div className='detalleSection'>
                            <h3>Estado del Pedido</h3>
                            <p><strong>Estado General:</strong> {mesaSeleccionada.estadoPedido || mesaSeleccionada.estado}</p>
                            <p><strong>Estado Cocina:</strong> {mesaSeleccionada.estado_cocina}</p>
                            <p><strong>Método de Pago:</strong> {mesaSeleccionada.pago}</p>
                        </div>

                        {mesaSeleccionada.productos && (
                            <div className='detalleSection'>
                                <h3>Productos</h3>
                                <div className='productosList'>
                                    {JSON.parse(mesaSeleccionada.productos).map((prod, index) => (
                                        <div key={index} className='productoItem'>
                                        <p className='productoNombre'>
                                            {prod.titulo} x{prod.cantidad}
                                        </p>
                                        <p className='productoPrecio'>
                                            {formatPrice(prod.precio * prod.cantidad)}
                                        </p>
                                        </div>
                                    ))}
                                </div>
                                <div className='totalPedido'>
                                    <strong>Total: {formatPrice(mesaSeleccionada.total)}</strong>
                                </div>
                            </div>
                        )}

                        {mesaSeleccionada.nota && (
                            <div className='detalleSection'>
                                <h3>Nota</h3>
                                <p>{mesaSeleccionada.nota}</p>
                            </div>
                        )}

                        <div className='modalActions'>
                            <button
                                onClick={() =>
                                    handleCambiarEstadoMesa(mesaSeleccionada.idMesa, 'Disponible')
                                }
                                className='btnDisponible'
                            >
                                Marcar como Disponible
                            </button>

                            <select
                                onChange={(e) =>
                                    handleCambiarEstadoPedido(mesaSeleccionada.idPedido, e.target.value)
                                }
                                className='selectEstado'
                                defaultValue=''
                            >
                                <option value='' disabled>
                                    Cambiar estado del pedido
                                </option>
                                <option value='Pendiente'>Pendiente</option>
                                <option value='Entregado'>Entregado</option>
                                <option value='Pagado'>Pagado</option>
                                <option value='Rechazado'>Rechazado</option>
                            </select>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Modal de edición */}
            <Modal open={modalEdit} onClose={() => setModalEdit(false)} center>
                <div className='modalEditContent'>
                    <h2>Editar acomodación</h2>
                    <form onSubmit={handleActualizarMesa}>
                        <div className='inputGroup'>
                            <label>Nombre de acomodación</label>
                            <input
                                type='text'
                                value={numeroEdit}
                                onChange={(e) => setNumeroEdit(e.target.value)}
                            />
                        </div>
                        <div className='inputGroup'>
                            <label>Capacidad (personas)</label>
                            <input
                                type='number'
                                min='1'
                                value={capacidadEdit}
                                onChange={(e) => setCapacidadEdit(e.target.value)}
                            />
                        </div>
                        <button type='submit' className='btnSubmit'>
                            Actualizar
                        </button>
                    </form>
                </div>
            </Modal>

            {/* Modal de nueva acomodación */}
            <Modal open={modalNew} onClose={() => setModalNew(false)} center>
                <div className='modalEditContent'>
                    <h2>Nueva acomodación</h2>
                    <form onSubmit={handleCrearMesa}>
                        <div className='inputGroup'>
                            <label>Nombre de acomodación</label>
                            <input
                                type='text'
                                value={numeroNew}
                                onChange={(e) => setNumeroNew(e.target.value)}
                                placeholder='Ej: 1, 2, A1, B2...'
                            />
                        </div>
                        <div className='inputGroup'>
                            <label>Capacidad (personas)</label>
                            <input
                                type='number'
                                min='1'
                                value={capacidadNew}
                                onChange={(e) => setCapacidadNew(e.target.value)}
                                placeholder='Número de personas'
                            />
                        </div>
                        <button type='submit' className='btnSubmit'>
                            Crear acomodación
                        </button>
                    </form>
                </div>
            </Modal>
        </div>
    );
}

